import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  InputSearch,
  TableData,
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  ConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from '@/components';
import { Column } from '@/types/common';
import {
  getPackages,
  getPackagesSummary,
  cancelPackage,
  PackageRow,
  PackageListParams,
  PackagesSummary,
  PACKAGE_STATUSES,
  PackageStatus,
} from '@/services/api/packages';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { useAuth } from '@/contexts/AuthContext';
import EditPackageModal from './EditPackageModal';
import PackageDetailDrawer from './PackageDetailDrawer';
import {
  ConditionBadge,
  PackageStatusBadge,
  STATUS_LABELS,
} from './PackageStatusBadge';
// Lazy-loaded so the react-pdf bundle (~489 kB) is only fetched when a user
// actually opens the label modal, not on initial app load.
const LabelPreviewModal = lazy(() => import('@/components/LabelPreviewModal'));

const MAX_BULK_PRINT = 50;

// dayjs renders the UTC ISO timestamp in the browser-local timezone (= the
// user's timezone), matching the shipments/deliveries "Created" columns.
const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : '—';

// Weight/volume stay editable through the hub-side lifecycle, but a package can
// only be cancelled while still CREATED (matches the backend rules). Cancelled
// rows are shown muted with all actions hidden.
const EDITABLE_STATUSES: PackageStatus[] = ['CREATED', 'HUB_SCANNED'];
const CANCELLABLE_STATUSES: PackageStatus[] = ['CREATED'];

/**
 * One dashboard card. Clickable cards double as status filters — the active one
 * gets the primary border, mirroring the prototype. `onClick` absent =
 * display-only.
 */
const SummaryCard: React.FC<{
  label: string;
  count: number | null;
  dotClass: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, count, dotClass, active, onClick }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 text-[13px] font-medium text-text-sub-600">
        <span className={`h-2 w-2 rounded-full ${dotClass}`} />
        {label}
      </div>
      <div className="mt-1.5 text-3xl font-bold tracking-tight text-text-strong-950 tabular-nums">
        {count ?? '—'}
      </div>
    </>
  );
  const base =
    'rounded-xl border bg-bg-white-0 px-5 py-4 text-left shadow-card-xs';
  const borderCls = active
    ? 'border-primary ring-1 ring-primary'
    : 'border-stroke-soft-200';
  if (!onClick) return <div className={`${base} ${borderCls}`}>{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${borderCls} transition-colors hover:border-primary`}
    >
      {inner}
    </button>
  );
};

const PackagesPage: React.FC = () => {
  // The authenticated client (company) — its name is stamped on the printed
  // label header ("DASH | {clientName}").
  const { client } = useAuth();
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);

  const [filters] = useState<PackageListParams>({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PackageStatus | ''>('');

  // Selection + print targeting are keyed by the unique item id (record.id),
  // NOT the PO — one PO/invoice can span several item rows on different
  // waybills, so keying by PO would pull siblings and print extra labels.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelTargetIds, setLabelTargetIds] = useState<string[]>([]);

  // Manage (edit / cancel) state.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editPkg, setEditPkg] = useState<PackageRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PackageRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Row click opens the read-only detail drawer (package info + POD photos).
  const [detailPkg, setDetailPkg] = useState<PackageRow | null>(null);

  // Dashboard cards (Semua PO / Delivered / Return). Client-scoped by auth;
  // refreshed on mount and after a mutation (cancel / edit) — not on list
  // filters, since the cards sit above them and count the whole scope.
  const [summary, setSummary] = useState<PackagesSummary | null>(null);
  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await getPackagesSummary());
    } catch {
      // Non-blocking: cards just keep their previous value (or em-dash).
    }
  }, []);
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPackages({
        ...filters,
        search: search || undefined,
        status: (statusFilter as PackageStatus) || undefined,
        page,
        limit: pageSize,
      });
      setPackages(res.packages ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      showErrorToast('Gagal memuat paket', getBackendErrorMessage(err));
      setPackages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, search, statusFilter, page, pageSize]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const togglePkg = (id: string) =>
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  // Cancelled rows aren't selectable (nothing to print/act on).
  const selectablePackages = packages.filter((p) => p.status !== 'CANCELLED');

  const selAllPkg = (checked: boolean) =>
    setSelectedIds(
      checked ? new Set(selectablePackages.map((p) => p.id)) : new Set()
    );

  const openLabelModal = (ids: string[]) => {
    setLabelTargetIds(ids);
    setLabelModalOpen(true);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelPackage(cancelTarget.id);
      showSuccessToast(
        'Paket dibatalkan',
        `${cancelTarget.itemName || cancelTarget.po} telah dibatalkan.`
      );
      // Drop it from any pending print selection, then refresh the list.
      setSelectedIds((prev) => {
        const s = new Set(prev);
        s.delete(cancelTarget.id);
        return s;
      });
      setCancelTarget(null);
      fetchPackages();
      fetchSummary();
    } catch (err) {
      showErrorToast('Gagal membatalkan', getBackendErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  const printSelected = () => {
    if (selectedIds.size === 0) {
      showErrorToast('Tidak ada PO dipilih', 'Pilih PO dulu untuk dicetak.');
      return;
    }
    if (selectedIds.size > MAX_BULK_PRINT) {
      showErrorToast(
        'Terlalu banyak PO',
        `Maksimum ${MAX_BULK_PRINT} PO per cetak massal. Pilih ${MAX_BULK_PRINT} atau kurang.`
      );
      return;
    }
    openLabelModal(Array.from(selectedIds));
  };

  const allSelected =
    selectablePackages.length > 0 &&
    selectablePackages.every((p) => selectedIds.has(p.id));

  const columns: Column<PackageRow>[] = [
    {
      key: 'po',
      title: (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-stroke-soft-200 accent-primary"
            checked={allSelected}
            onChange={(e) => selAllPkg(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
          <span>PO / Item</span>
        </div>
      ),
      render: (_v, record) => (
        <div className="flex items-start gap-2">
          {record.status === 'CANCELLED' ? (
            // Keep the column aligned without an actionable checkbox.
            <span className="mt-0.5 inline-block h-4 w-4 flex-shrink-0" />
          ) : (
            <input
              type="checkbox"
              className="mt-0.5 rounded border-stroke-soft-200 accent-primary flex-shrink-0"
              checked={selectedIds.has(record.id)}
              onChange={() => togglePkg(record.id)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div>
            <div
              className="font-mono text-xs font-semibold text-primary"
              style={{ wordBreak: 'break-all' }}
            >
              {record.po}
            </div>
            {record.itemName && record.itemName !== record.po && (
              <div className="text-xs text-text-soft-400 mt-0.5">
                {record.itemName}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      render: (_v, record) => (
        <span className="text-xs text-text-sub-600">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, record) => (
        <span className="font-mono text-xs text-text-sub-600">
          {record.bookingId || '—'}
        </span>
      ),
    },
    {
      key: 'outlet',
      title: 'Recipient',
      render: (_v, record) => (
        <span className="text-sm font-medium text-text-strong-950">
          {record.outlet}
        </span>
      ),
    },
    {
      key: 'sender',
      title: 'Sender',
      render: (_v, record) => (
        <span className="text-sm text-text-sub-600">{record.sender}</span>
      ),
    },
    {
      key: 'qty',
      title: 'Qty',
      render: (_v, record) => (
        <span className="text-sm text-text-strong-950 tabular-nums">
          {record.qty}
        </span>
      ),
    },
    {
      key: 'koli',
      title: 'Koli',
      render: (_v, record) => (
        <span className="text-sm text-text-strong-950 tabular-nums">
          {record.koli ?? '—'}
        </span>
      ),
    },
    {
      key: 'weight',
      title: 'Weight',
      render: (_v, record) => (
        <span className="text-sm text-text-sub-600 tabular-nums">
          {record.weight} kg
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      // Damage is a condition, not a status (Status Model v2 §4) — a damaged
      // package keeps its real stage and gets an extra badge beside it.
      render: (_v, record) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <PackageStatusBadge status={record.status} />
          <ConditionBadge condition={record.condition} />
        </div>
      ),
    },
    {
      key: 'actions' as keyof PackageRow,
      title: '',
      render: (_v, record) => {
        // Cancelled rows expose no actions.
        if (record.status === 'CANCELLED') return null;

        const canEdit = EDITABLE_STATUSES.includes(record.status);
        const canCancel = CANCELLABLE_STATUSES.includes(record.status);
        const menuActions: ActionDropdownAction[] = [
          ...(canEdit
            ? [
                {
                  key: 'edit',
                  label: 'Edit weight, volume & koli',
                  icon: 'ri-edit-line',
                  onClick: () => {
                    setEditPkg(record);
                    setOpenMenuId(null);
                  },
                },
              ]
            : []),
          ...(canCancel
            ? [
                {
                  key: 'cancel',
                  label: 'Cancel package',
                  icon: 'ri-close-circle-line',
                  className: 'text-error-base hover:bg-error-lighter',
                  onClick: () => {
                    setCancelTarget(record);
                    setOpenMenuId(null);
                  },
                },
              ]
            : []),
        ];

        return (
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stroke-soft-200 text-xs font-medium text-text-sub-600 hover:bg-bg-weak-50 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                openLabelModal([record.id]);
              }}
            >
              <i className="ri-printer-line text-sm" />
              Print
            </button>
            {/* Fixed-width slot so the Print button stays column-aligned whether
                or not this row has a 3-dot menu. */}
            <div className="flex w-7 shrink-0 justify-center">
              {menuActions.length > 0 && (
                <ActionDropdown
                  record={record}
                  isOpen={openMenuId === record.id}
                  onToggle={() =>
                    setOpenMenuId((cur) =>
                      cur === record.id ? null : record.id
                    )
                  }
                  actions={menuActions}
                  ariaLabel={`Manage ${record.po}`}
                />
              )}
            </div>
          </div>
        );
      },
    },
  ];

  const modalPackages = packages
    .filter((p) => labelTargetIds.includes(p.id))
    .map((p) => ({
      ...p,
      client:
        p.client ?? (client ? { id: client.id, name: client.name } : undefined),
    }));

  return (
    <div className="p-4 space-y-5 sm:p-6">
      {/* Page head */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-bold text-primary">03</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400">
              Packages
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-strong-950 sm:text-3xl">
            Packages
          </h1>
          <p className="max-w-2xl text-sm text-text-sub-600">
            Cetak label PO untuk tiap paket — tempel di paket agar bisa di-scan
            via QR oleh tim hub &amp; driver. Pilih beberapa PO untuk cetak
            massal dalam satu dokumen.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-3">
          <button
            type="button"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: 'var(--color-primary, #5e2aac)' }}
            onClick={printSelected}
          >
            <i className="ri-printer-line text-base" />
            {selectedIds.size > 0
              ? `Print labels (${selectedIds.size})`
              : 'Print labels'}
          </button>
        </div>
      </div>

      {/* Dashboard cards, all four clickable as status filters. Keys follow
          Status Model v2 (docs/09-FE-CHANGES.md §2): `delivered` became
          `completed`, and the single `returned` bucket split into `inReturn` /
          `returnedToHub` / `returnedToClient` — the two the client cares about
          get a card each. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Semua Packages"
          count={summary ? (summary.total ?? 0) : null}
          dotClass="bg-primary"
          active={statusFilter === ''}
          onClick={() => {
            setStatusFilter('');
            setPage(1);
          }}
        />
        <SummaryCard
          label="Completed"
          count={summary ? (summary.completed ?? 0) : null}
          dotClass="bg-success-base"
          active={statusFilter === 'COMPLETED'}
          onClick={() => {
            setStatusFilter('COMPLETED');
            setPage(1);
          }}
        />
        <SummaryCard
          label="In Return"
          count={summary ? (summary.inReturn ?? 0) : null}
          dotClass="bg-warning-base"
          active={statusFilter === 'IN_RETURN'}
          onClick={() => {
            setStatusFilter('IN_RETURN');
            setPage(1);
          }}
        />
        <SummaryCard
          label="Returned to Hub"
          count={summary ? (summary.returnedToHub ?? 0) : null}
          dotClass="bg-error-base"
          active={statusFilter === 'RETURNED_TO_HUB'}
          onClick={() => {
            setStatusFilter('RETURNED_TO_HUB');
            setPage(1);
          }}
        />
      </div>

      {/* Table card */}
      <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke-soft-200 p-4">
          <InputSearch
            className="min-w-[200px] max-w-[380px] flex-1"
            placeholder="Search PO, Booking ID, outlet…"
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
          />
          <div className="flex items-center gap-2">
            <select
              className="rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 py-2 text-sm text-text-sub-600 focus:outline-none focus:ring-2 focus:ring-primary"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as PackageStatus | '');
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {PACKAGE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <TableData<PackageRow>
          data={packages}
          columns={columns}
          loading={loading}
          className="rounded-none border-0 shadow-none"
          onRowClick={(record) => {
            // Cancelled rows are inert; everything else opens the detail drawer
            // (package info + POD photos). Printing stays on the Print button.
            if (record.status !== 'CANCELLED') setDetailPkg(record);
          }}
          rowClassName={(record) =>
            record.status === 'CANCELLED'
              ? 'opacity-55 [&_*]:!text-text-soft-400'
              : ''
          }
        />

        <div className="px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      </div>

      {labelModalOpen && (
        <Suspense fallback={null}>
          <LabelPreviewModal
            open={labelModalOpen}
            onClose={() => setLabelModalOpen(false)}
            packages={modalPackages}
          />
        </Suspense>
      )}

      <PackageDetailDrawer pkg={detailPkg} onClose={() => setDetailPkg(null)} />

      <EditPackageModal
        pkg={editPkg}
        open={editPkg !== null}
        onClose={() => setEditPkg(null)}
        onSaved={() => {
          fetchPackages();
          fetchSummary();
        }}
      />

      <ConfirmDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancel this package?"
        message={
          cancelTarget
            ? `"${cancelTarget.itemName || cancelTarget.po}" will be marked cancelled and removed from its shipment totals. This can't be undone.`
            : ''
        }
        confirmLabel="Cancel package"
        cancelLabel="Keep package"
        tone="danger"
        loading={cancelling}
      />
    </div>
  );
};

export default PackagesPage;
